import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { 
  signIn, 
  signUp, 
  confirmSignUp, 
  confirmResetPassword,
  resetPassword, 
  signOut, 
  getCurrentUser, 
  fetchUserAttributes,
  updateUserAttributes,
  fetchAuthSession
} from 'aws-amplify/auth';
import { environment } from '../../environment/environment';
import { IUser } from '../models/i-user';
import { UserRoles } from '../models/user-roles.enum';
import { IAuthService } from '../core/auth-utils/i-auth-service';

@Injectable({
  providedIn: 'root',
})
export class CognitoService extends IAuthService {
  private user = {} as IUser;
  
  constructor() {
    super();
    
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognito.userPoolId,
          userPoolClientId: environment.cognito.userPoolWebClientId,
          loginWith: {
            email: true,
            username: true
          }
        }
      }
    });
  }

  signUp(user: IUser): Promise<any> {
    return signUp({
      username: user.name!,
      password: user.password!,
      options: {
        userAttributes: {
          email: user.email,
        },
      }
    });
  }

  confirmSignUp(user: IUser): Promise<any> {
    return confirmSignUp({
      username: user.email,
      confirmationCode: user.code!
    });
  }

  signIn(user: IUser): Promise<any> {
    return signIn({
      username: user.name || user.email,
      password: user.password!
    }).then(
      async (result: any): Promise<any> => {
        if (result.isSignedIn) {
          await this.getUser();
          this.authenticationSubject.next(true);
          return { user: this.user, action: 'SIGNED_IN' };
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          return { user: result, action: 'NEW_PASSWORD_REQUIRED' };
        }
        
        return { user: result, action: 'UNKNOWN' };
      }
    );
  }

  completeSignIn(user: any, password: string): Promise<any> {
    // TODO need to be handled differently in v6
    return Promise.reject('completeNewPassword not implemented yet');
  }

  sendForgotPasswordCode(user: any): Promise<any> {
    return resetPassword({ username: user });
  }

  resetPassword(
    username: any,
    code: string,
    newPassword: string
  ): Promise<void> {
    return confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword
    });
  }

  signOut(): Promise<any> {
    return signOut().then(() => {
      this.user = {} as IUser;
      this.authenticationSubject.next(false);
    }).catch((error) => {
      this.user = {} as IUser;
      this.authenticationSubject.next(false);
      throw error;
    });
  }

  isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    } else {
      return this.getUser()
        .then((user: any) => {
          if (user && user.email) {
            this.authenticationSubject.next(true);
            return true;
          }
          return false;
        })
        .catch(() => {
          this.authenticationSubject.next(false);
          return false;
        });
    }
  }

  async getUser(): Promise<IUser> {
    try {
      this.user = {} as IUser;
      
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      
      if (!currentUser || !userAttributes) {
        throw new Error('No authenticated user');
      }

      this.user.id = currentUser.userId;
      this.user.email = userAttributes.email || '';
      this.user.firstName = userAttributes.given_name || '';
      this.user.lastName = userAttributes.family_name || '';
      this.user.name = currentUser.username;

      const idToken = session.tokens?.idToken;
      if (idToken) {
        const payload = idToken.payload;
        const groups = payload['cognito:groups'] as string[] || [];
        this.user.role = this.determineUserRole(groups);
      } else {
        this.user.role = UserRoles.PENDING_USER;
      }

      return this.user;
    } catch (error) {
      this.user = {} as IUser;
      throw error;
    }
  }

  private determineUserRole(groups: string[]): UserRoles {
    if (groups.includes(environment.cognito.groups.globalAdmin)) {
      return UserRoles.GLOBAL_ADMIN;
    }
    if (groups.includes(environment.cognito.groups.pendingUser)) {
      return UserRoles.PENDING_USER;
    }
    if (groups.includes(environment.cognito.groups.technicianAdmin)) {
      return UserRoles.TECHNICIAN_ADMIN;
    }
    if (groups.includes(environment.cognito.groups.technician)) {
      return UserRoles.TECHNICIAN;
    }
    if (groups.includes(environment.cognito.groups.swManager)) {
      return UserRoles.SW_MANAGER;
    }
    if (groups.includes(environment.cognito.groups.swDeveloper)) {
      return UserRoles.SW_DEVELOPER;
    }
    return UserRoles.PENDING_USER;
  }

  async currentAuthenticatedUser(): Promise<any> {
    return getCurrentUser();
  }

  updateUser(user: IUser): Promise<any> {
    return updateUserAttributes({
      userAttributes: {
        email: user.email,
        given_name: user.firstName,
        family_name: user.lastName
      }
    });
  }

  canAccessRoutes(): boolean {
    return this.user.role !== UserRoles.PENDING_USER && 
           this.authenticationSubject.value;
  }

  shouldRedirectToPending(): boolean {
    return this.user.role === UserRoles.PENDING_USER;
  }
}
